/**
 * Copy for the OAuth 2.1 consent surface, ported from the xelerate-ai
 * reference (`i18n/locales/en/oauthConsent.json`) with the product name
 * swapped to PX. This client ships no i18n runtime, so the strings live in one
 * module rather than in a component — one place to translate the day PX grows
 * a locale layer, and nothing for a page to hardcode.
 *
 * The sign-in block has no counterpart in the reference: xelerate bounces an
 * unauthenticated visitor to its real `/sign-in` page, whereas PX's `/login` is
 * a prototype persona picker that authenticates nobody, so the consent surface
 * carries its own credential step (decision D7).
 */
export const CONSENT_COPY = {
  /** Headline. `client` is the AS-resolved display name, never raw user input. */
  title: (client: string) => `Connect ${client} to PX`,
  fallbackClient: 'this application',
  subtitle:
    'This application is requesting access to act on your behalf in PX.',
  scope: {
    title: 'Act as you across PX',
    body: 'Read and update the projects, tickets, and data you can already access - always limited to your own permissions.',
  },
  unverified: {
    title: 'Unverified app',
    body: 'This app was registered by a third party and has not been verified by PX. Only continue if you trust it.',
  },
  reassurance:
    'You can revoke this access at any time from your connected apps. PX never shares your password with this application.',
  allow: 'Allow access',
  deny: 'Deny',
  submitting: 'Processing...',
  error:
    'Something went wrong while saving your decision. Please try again.',
  invalid: {
    title: 'Invalid request',
    body: 'This authorization link is incomplete or has expired. Start the connection again from the application you were using.',
  },
  signIn: {
    heading: 'Sign in to continue',
    lede: 'Confirm who you are before deciding whether to connect this application.',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in...',
    missingFields: 'Enter your email and password.',
    invalidCredentials: 'That email and password did not match an account.',
    twoFactor:
      'This account requires a second factor, which this screen cannot collect yet. Sign in to PX first, then retry the connection.',
    failed: 'Sign-in could not be completed. Please try again.',
  },
} as const
